import { useState, useRef, useCallback } from 'react';

/**
 * Obtiene el identificador único del item: preferir `key` (canonical del backend), sino `jobName`.
 */
function getItemId(item) {
  return (item.key && item.key.trim()) ? item.key : item.jobName;
}

/**
 * Hook para persistir la selección de llamadas entre páginas.
 * Usa un Map interno con key (del backend) o jobName como clave.
 * Almacena siempre la prop `key` cuando el backend la devuelve.
 */
export function useSelectionPersistence() {
  const mapRef = useRef(new Map());
  const [totalSelectedCount, setTotalSelectedCount] = useState(0);

  const syncCount = useCallback(() => {
    setTotalSelectedCount(mapRef.current.size);
  }, []);

  const addItems = useCallback((items) => {
    let changed = false;
    for (const item of items) {
      const id = getItemId(item);
      if (!id) continue;
      if (!mapRef.current.has(id)) {
        mapRef.current.set(id, {
          id: item.id,
          jobName: item.jobName,
          key: item.key || null,
          supervisor: item.supervisor,
          agent: item.agent,
          timestamp: item.timestamp,
        });
        changed = true;
      }
    }
    if (changed) syncCount();
  }, [syncCount]);

  const removeItems = useCallback((items) => {
    let changed = false;
    for (const item of items) {
      const id = getItemId(item);
      if (id && mapRef.current.has(id)) {
        mapRef.current.delete(id);
        changed = true;
      }
    }
    if (changed) syncCount();
  }, [syncCount]);

  const clearAll = useCallback(() => {
    if (mapRef.current.size > 0) {
      mapRef.current.clear();
      syncCount();
    }
  }, [syncCount]);

  /**
   * Devuelve las keys seleccionadas para enviar al backend (selectedJobNames).
   * El backend filtra solo por key (no por jobName).
   */
  const getSelectedJobNames = useCallback(() => {
    return Array.from(mapRef.current.values()).map(v => v.key).filter(Boolean);
  }, []);

  /**
   * Dado los items de la página actual, devuelve los que están seleccionados
   * en el Map global. Esto se usa como prop `selectedItems` de CloudScape Table.
   */
  const getPageSelectedItems = useCallback((pageItems) => {
    if (!pageItems) return [];
    return pageItems.filter(item => {
      const id = getItemId(item);
      return id && mapRef.current.has(id);
    });
  }, []);

  /**
   * Reconcilia un evento onSelectionChange de CloudScape Table con el Map global.
   * `newPageSelected` son los items que CloudScape reporta como seleccionados
   * en la página actual. `currentPageItems` son todos los items de la página.
   */
  const handleSelectionChange = useCallback((newPageSelected, currentPageItems) => {
    if (!currentPageItems) return;

    const newSelectedIds = new Set(newPageSelected.map(i => getItemId(i)));
    let changed = false;

    for (const item of currentPageItems) {
      const id = getItemId(item);
      if (!id) continue;

      if (newSelectedIds.has(id)) {
        if (!mapRef.current.has(id)) {
          mapRef.current.set(id, {
            id: item.id,
            jobName: item.jobName,
            key: item.key || null,
            supervisor: item.supervisor,
            agent: item.agent,
            timestamp: item.timestamp,
          });
          changed = true;
        }
      } else {
        if (mapRef.current.has(id)) {
          mapRef.current.delete(id);
          changed = true;
        }
      }
    }

    if (changed) syncCount();
  }, [syncCount]);

  return {
    mapRef,
    totalSelectedCount,
    addItems,
    removeItems,
    clearAll,
    getSelectedJobNames,
    getPageSelectedItems,
    handleSelectionChange,
  };
}
